'use client';

import Image from 'next/image';
import Link from 'next/link';

import { ServiceAllCapsTitle } from '@/app/(pages)/(services)/components/ServiceAllCapsTitle';

const DESKTOP_IMAGE_WIDTH = 1078;

type ObjectPosition =
  | 'object-bottom'
  | 'object-center'
  | 'object-left-bottom'
  | 'object-left-top'
  | 'object-left'
  | 'object-right-bottom'
  | 'object-right-top'
  | 'object-right'
  | 'object-top';

/** Segment of a paragraph: plain text or an inline link. */
export type ParagraphSegment = { type: 'link'; href: string; text: string } | { type: 'text'; value: string };

/** A paragraph is either a plain string or an array of segments (for inline links). */
export type ParagraphContent = ParagraphSegment[] | string;

export interface AllTurtlesInActionProps {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundPosition?: ObjectPosition;
  ctaLink?: string;
  ctaText?: string;
  mobileBackgroundImage?: string;
  mobileBackgroundPosition?: ObjectPosition;
  paragraphs: ParagraphContent[];
  sectionTitle?: string;
  testimonialPerson?: string;
  testimonialPhoto?: string;
  testimonialPhotoAlt?: string;
  testimonialQuote?: string;
  testimonialTitle?: string;
  title: string;
}

function renderParagraphContent(content: ParagraphContent) {
  if (typeof content === 'string') {
    return content;
  }
  return content.map((segment, i) => {
    if (segment.type === 'text') {
      return <span key={i}>{segment.value}</span>;
    }
    return (
      <a key={i} href={segment.href} className="underline hover:no-underline" target="_blank" rel="noopener noreferrer">
        {segment.text}
      </a>
    );
  });
}

/**
 * "All Turtles in Action" section featuring:
 * - Mobile: title above image (block), then white content box, then testimonial
 * - Tablet+: background image with title + white box overlaid, then testimonial
 */
export function AllTurtlesInAction({
  backgroundColor = 'bg-navy',
  backgroundImage,
  backgroundPosition = 'object-left-top',
  ctaLink,
  ctaText,
  mobileBackgroundImage,
  mobileBackgroundPosition = 'object-center',
  paragraphs,
  sectionTitle = 'ALL TURTLES IN ACTION',
  title,
  testimonialPerson = '',
  testimonialPhoto = '',
  testimonialPhotoAlt = '',
  testimonialQuote = '',
  testimonialTitle = '',
}: AllTurtlesInActionProps) {
  const imageSrc = backgroundImage ?? '';
  const mobileImageSrc = mobileBackgroundImage || backgroundImage || '';
  const showTestimonial = Boolean(testimonialQuote?.trim() && testimonialPhoto);

  return (
    <section className="relative w-full overflow-x-hidden overflow-y-visible bg-navy">
      {/* Flex container: mobile = column with order (title, image, content); tablet = block with absolute image */}
      <div className="flex flex-col tablet:block">
        {/* Title above image on mobile only */}
        <div className="order-1 px-4 pt-8 tablet:order-none tablet:hidden">
          <ServiceAllCapsTitle title={sectionTitle} />
        </div>

        {/* Image: block on mobile, absolute background on tablet */}
        {imageSrc && (
          <div className="relative order-2 w-full tablet:absolute tablet:left-1/2 tablet:top-[96px] tablet:w-full tablet:max-w-[1200px] tablet:-translate-x-1/2">
            {/* Mobile: in-flow block image */}
            <div className="relative aspect-[3/2] w-full tablet:hidden">
              <Image
                src={mobileImageSrc}
                alt=""
                fill
                className={`object-cover object-center ${mobileBackgroundPosition}`}
                priority
                sizes="100vw"
              />
            </div>
            {/* Tablet: positioned image */}
            <div className="hidden w-full max-w-[1078px] tablet:block">
              <Image
                src={imageSrc}
                alt=""
                width={DESKTOP_IMAGE_WIDTH}
                height={800}
                className={`h-auto w-full max-w-[1078px] object-contain ${backgroundPosition}`}
                priority
                sizes="(max-width: 1200px) 100vw, 1078px"
                unoptimized
              />
            </div>
          </div>
        )}

        {/* Content: title (tablet only) + white box. Mobile: edge-to-edge, no gap above; tablet: centered overlay */}
        <div className="relative z-10 order-3 mt-0 flex justify-center px-0 tablet:order-none tablet:mt-[48px] tablet:px-0">
          <div className="flex w-full max-w-full flex-col tablet:w-full tablet:max-w-[1328px] tablet:flex-row tablet:justify-end">
            <div className="w-full tablet:w-full tablet:max-w-[640px]">
              <div className="hidden tablet:block">
                <ServiceAllCapsTitle title={sectionTitle} />
              </div>
              <div className="mt-0 w-full bg-white p-6 tablet:mt-16 tablet:w-full tablet:max-w-[640px] tablet:p-12">
                <p className="mb-0 font-heldane text-serviceBodyLarge font-normal leading-[1.2] text-navy">{title}</p>
                {paragraphs.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`font-soehne text-serviceBody font-normal leading-[150%] text-navy ${
                      index === 0 ? 'mt-6 tablet:mt-12' : 'mt-4 tablet:mt-[24px]'
                    }`}
                  >
                    {renderParagraphContent(paragraph)}
                  </p>
                ))}
                {ctaText && ctaLink && (
                  <Link
                    href={ctaLink}
                    className="mt-6 inline-block rounded-md bg-[#EB4646] px-5 py-3 font-soehne text-serviceCTAText font-normal leading-tight text-[#FAFAFA] transition-colors hover:bg-[#d63a3a] tablet:mt-8 tablet:px-8 tablet:py-4"
                  >
                    {ctaText}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer so desktop background image has room below the white box */}
      <div className="hidden tablet:block" style={{ minHeight: 120 }} aria-hidden />

      {/* Testimonial: only when quote and photo are provided */}
      {showTestimonial && (
        <div className="relative z-10 bg-navy pb-12 pt-0 tablet:pb-0 tablet:pt-[104px]">
          <div className="mx-auto w-full max-w-[1200px] px-0 tablet:px-0">
            <div className="flex flex-col gap-8 tablet:flex-row tablet:gap-24">
              <div className="w-full tablet:w-[582px] tablet:shrink-0">
                <div className="relative w-full tablet:w-[582px]">
                  <Image
                    src={testimonialPhoto}
                    alt={testimonialPhotoAlt || testimonialPerson}
                    width={582}
                    height={582}
                    className="h-auto w-full max-w-full object-cover object-center tablet:w-[582px]"
                    sizes="(max-width: 766px) 100vw, 582px"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center px-4 tablet:px-0">
                <blockquote className="mb-4 font-heldane text-serviceQuote font-normal leading-[150%] text-[#FFF] tablet:mb-6">
                  &ldquo;{testimonialQuote}&rdquo;
                </blockquote>
                <div className="mt-2 tablet:mt-0">
                  <p className="mb-1 font-soehne text-serviceCaption font-semibold text-white">
                    – {testimonialPerson}, {testimonialTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
